import { useState, useEffect, useRef, useCallback } from "react";

interface UseTypewriterEffectOptions {
  /** Speed of typewriter effect in milliseconds per character */
  speed?: number;
  /** Whether to start typewriter effect immediately */
  startImmediately?: boolean;
  /** Callback when typewriter finishes displaying current content */
  onComplete?: () => void;
}

export function useTypewriterEffect(
  targetText: string,
  options: UseTypewriterEffectOptions = {}
) {
  const {
    speed = 20, // Very fast for real-time feel
    startImmediately = true,
    onComplete,
  } = options;

  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentIndexRef = useRef(0);
  const targetTextRef = useRef(targetText);

  // Update target text ref when targetText changes
  useEffect(() => {
    targetTextRef.current = targetText;
  }, [targetText]);

  const startTypewriter = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    setIsTyping(true);

    intervalRef.current = setInterval(() => {
      const currentTarget = targetTextRef.current;
      const currentIndex = currentIndexRef.current;

      if (currentIndex >= currentTarget.length) {
        // Finished typing current content
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        setIsTyping(false);
        onComplete?.();
        return;
      }

      // Add next character(s) - we can add multiple chars at once for speed
      const charsToAdd = Math.min(2, currentTarget.length - currentIndex); // Add 1-2 chars at a time
      const nextText = currentTarget.slice(0, currentIndex + charsToAdd);

      setDisplayedText(nextText);
      currentIndexRef.current += charsToAdd;
    }, speed);
  }, [speed, onComplete]);

  const stopTypewriter = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsTyping(false);
    setDisplayedText(targetText);
    currentIndexRef.current = targetText.length;
  }, [targetText]);

  const reset = useCallback(() => {
    stopTypewriter();
    setDisplayedText("");
    currentIndexRef.current = 0;
  }, [stopTypewriter]);

  // Auto-start when content changes
  useEffect(() => {
    if (targetText.length > currentIndexRef.current && startImmediately) {
      startTypewriter();
    }
  }, [targetText, startTypewriter, startImmediately]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    displayedText,
    isTyping,
    startTypewriter,
    stopTypewriter,
    reset,
    progress:
      targetText.length > 0 ? currentIndexRef.current / targetText.length : 0,
  };
}


